import { isEmpty } from '@ember/utils'
import type { NormalizedInterval } from 'date-fns'
import type { StudentsScope } from 'teacher-ui-client/reporting/ui-scope/utils'
import type StudentEventsService from 'teacher-ui-client/services/student-events'
import fillMonths from 'teacher-ui-client/utils/student-events/fill-months'

export interface AwardsEarnedByMonthEventsResponse {
  awards_earned_by_month_events_summary: SummaryData
  awards_earned_by_month_events: MonthData[]
}

interface SummaryData {
  data: [
    { category: 'gold'; count: number },
    { category: 'silver'; count: number },
    { category: 'bronze'; count: number },
  ]
}

interface MonthData {
  gold: number
  silver: number
  bronze: number
  month: string
}

export interface AwardsEarnedByMonthEventsData {
  summary: {
    gold: number
    silver: number
    bronze: number
  }
  months: {
    gold: number
    silver: number
    bronze: number
    month: string
  }[]
}

export async function awardsEarnedByMonthEvents(
  this: StudentEventsService,
  product: LegacyProductRe | LegacyProductMs,
  statsScope: StudentsScope,
  period: NormalizedInterval,
): Promise<Nullable<AwardsEarnedByMonthEventsData>> {
  if (isEmpty(statsScope.ids)) return null

  const url = this.buildUrl('awards_earned_by_month_events', product, statsScope)
  const query = this.buildQuery(statsScope, period)

  const data = await this.fetchData<AwardsEarnedByMonthEventsResponse>(url, query)
  if (!data) return null

  const { awards_earned_by_month_events_summary: summary, awards_earned_by_month_events: months } = data
  if (months.length === 0) {
    return null
  }

  const goldCount = summary.data.find((value) => value.category === 'gold')?.count ?? 0
  const silverCount = summary.data.find((value) => value.category === 'silver')?.count ?? 0
  const bronzeCount = summary.data.find((value) => value.category === 'bronze')?.count ?? 0
  const fullMonthData = fillMonths(months, period, {
    gold: 0,
    silver: 0,
    bronze: 0,
  })

  return {
    summary: {
      gold: goldCount,
      silver: silverCount,
      bronze: bronzeCount,
    },
    months: fullMonthData,
  }
}
